import { Surface } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowUpRight, ArrowDownRight, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const TZ = "Europe/Copenhagen";
export const PLAN_LABELS = { gratis: "Free", start: "Starter", pro: "Pro", business: "Business" };

export function fmtDKK(n) {
  if (n === null || n === undefined) return "—";
  try { return new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 0 }).format(n); }
  catch { return `${Math.round(n)} kr.`; }
}
export function fmtDate(iso, withTime = false) {
  if (!iso) return "—";
  const d = typeof iso === "number" && iso < 1e12 ? iso * 1000 : iso;
  try { return new Date(d).toLocaleString("da-DK", { timeZone: TZ, dateStyle: "medium", ...(withTime ? { timeStyle: "short" } : {}) }); }
  catch { return "—"; }
}
export function fmtNum(n) { return n === null || n === undefined ? "—" : new Intl.NumberFormat("da-DK").format(n); }

export function Kpi({ label, value, change, tip, isMoney }) {
  const shown = value === null || value === undefined ? "—" : typeof value === "string" ? value : (isMoney ? fmtDKK(value) : fmtNum(value));
  const up = change > 0, down = change < 0;
  return (
    <Surface testid={`kpi-${label}`} className="!p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground leading-tight">{label}</span>
        {tip && (
          <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 cursor-help" /></TooltipTrigger>
            <TooltipContent className="max-w-[240px] text-xs">{tip}</TooltipContent></Tooltip></TooltipProvider>
        )}
      </div>
      <p className="font-display text-3xl font-medium mt-3">{shown}</p>
      {change !== null && change !== undefined ? (
        <div className={`mt-1 flex items-center gap-1 text-xs ${up ? "text-emerald-500" : down ? "text-rose-500" : "text-muted-foreground"}`}>
          {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : down ? <ArrowDownRight className="h-3.5 w-3.5" /> : null}
          {Math.abs(change)}% vs. forrige periode
        </div>
      ) : <div className="mt-1 text-xs text-muted-foreground">&nbsp;</div>}
    </Surface>
  );
}

export function FilterBar({ filters, setFilters, showSource, hideFree }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-filter-bar">
      <Select value={String(filters.days)} onValueChange={(v) => set("days", Number(v))}>
        <SelectTrigger className="w-[130px] h-9" data-testid="filter-period"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="7">7 dage</SelectItem>
          <SelectItem value="30">30 dage</SelectItem>
          <SelectItem value="90">90 dage</SelectItem>
        </SelectContent>
      </Select>
      <Select value={filters.plan || "all"} onValueChange={(v) => set("plan", v === "all" ? "" : v)}>
        <SelectTrigger className="w-[130px] h-9" data-testid="filter-plan"><SelectValue placeholder="Alle planer" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle planer</SelectItem>
          {Object.entries(PLAN_LABELS).filter(([k]) => !(hideFree && k === "gratis")).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex items-center gap-2 ml-auto px-3 h-9 rounded-md border border-border">
        <span className="text-xs text-muted-foreground">Vis testdata</span>
        <Switch checked={filters.show_test} onCheckedChange={(v) => set("show_test", v)} data-testid="filter-show-test" />
      </div>
    </div>
  );
}

export function FunnelView({ steps }) {
  const max = Math.max(...steps.filter((s) => s.value != null).map((s) => s.value), 1);
  return (
    <div className="space-y-2" data-testid="funnel-view">
      {steps.map((s, i) => {
        const pct = s.value != null ? (s.value / max) * 100 : 0;
        return (
          <div key={s.key} className="flex items-center gap-3">
            <div className="w-40 shrink-0 text-sm text-muted-foreground">{i + 1}. {s.label}</div>
            <div className="flex-1 h-9 rounded-lg bg-secondary/60 relative overflow-hidden">
              {s.value != null ? (
                <div className="h-full rounded-lg flex items-center px-3 text-sm font-medium" style={{ width: `${Math.max(pct, 8)}%`, backgroundColor: "#1dddbf22", color: "inherit", borderRight: "2px solid #1dddbf" }}>
                  {fmtNum(s.value)}
                </div>
              ) : (
                <div className="h-full flex items-center px-3 text-xs text-muted-foreground italic">— {s.ga4_only ? "kun i GA4" : "ingen data"}</div>
              )}
            </div>
            <div className="w-20 shrink-0 text-right text-xs text-muted-foreground">
              {s.conv_from_prev != null ? `${s.conv_from_prev}%` : ""}
            </div>
          </div>
        );
      })}
    </div>
  );
}
